import { apiRequest } from './client'
import type {
  SilkAddEpisodeBody,
  SilkCopyWorkProcessResult,
  SilkCreateWorkBody,
  SilkEpisode,
  SilkEpisodeList,
  SilkUpdateEpisodeBody,
  SilkUpdateWorkBody,
  SilkWork,
  SilkWorkList,
  SilkWorkProcess,
  SilkWorkStatus,
} from './types'

export const worksApi = {
  /** 公开作品列表(无需登录) */
  publicList: (params?: {
    category?: string
    category_code?: string
    order_by?: 'published_at' | 'view_count' | 'like_count' | 'created_at' | 'curated'
    limit?: number
    offset?: number
  }) => apiRequest<SilkWorkList>('/api/v1/works', { query: params, auth: false }),

  /** 作品详情(id 支持数字主键或 public_id) */
  get: (id: string | number) =>
    apiRequest<SilkWork>(`/api/v1/works/${encodeURIComponent(String(id))}`, { auth: true }),

  episodes: (id: string | number, params?: { limit?: number; offset?: number }) =>
    apiRequest<SilkEpisodeList>(`/api/v1/works/${encodeURIComponent(String(id))}/episodes`, {
      auth: true,
      query: params,
    }),

  /** 累加当月观看秒数（公开；登录且订阅时计入 subscriber_watch_sec） */
  recordWatch: (id: string | number, seconds: number) =>
    apiRequest<{ ok: boolean }>(`/api/v1/works/${encodeURIComponent(String(id))}/watch`, {
      method: 'POST',
      body: { seconds },
    }),

  /** 切换点赞（须登录；仅 published 作品） */
  toggleLike: (id: string | number) =>
    apiRequest<{ liked: boolean; like_count: number }>(
      `/api/v1/works/${encodeURIComponent(String(id))}/like`,
      { method: 'POST' },
    ),

  /** 公开制作过程（画布预览 + 资产）；无关联时 available=false */
  getProcess: (id: string | number) =>
    apiRequest<SilkWorkProcess>(`/api/v1/works/${encodeURIComponent(String(id))}/process`, {
      auth: false,
    }),

  /** 复刻制作过程到我的创作空间（须登录 + studio_creative） */
  copyProcess: (id: string | number) =>
    apiRequest<SilkCopyWorkProcessResult>(
      `/api/v1/works/${encodeURIComponent(String(id))}/process/copy`,
      { method: 'POST' },
    ),

  /** 我的作品(登录) */
  myList: (params?: {
    status?: SilkWorkStatus
    category_code?: string
    limit?: number
    offset?: number
  }) => apiRequest<SilkWorkList>('/api/v1/works/me', { query: params }),

  create: (body: SilkCreateWorkBody) =>
    apiRequest<SilkWork>('/api/v1/works', { method: 'POST', body }),

  update: (id: number, body: SilkUpdateWorkBody) =>
    apiRequest<SilkWork>(`/api/v1/works/${id}`, { method: 'PATCH', body }),

  remove: (id: number) =>
    apiRequest<{ ok: boolean }>(`/api/v1/works/${id}`, { method: 'DELETE' }),

  submit: (id: number) => apiRequest<SilkWork>(`/api/v1/works/${id}/submit`, { method: 'POST' }),

  publish: (id: number) => apiRequest<SilkWork>(`/api/v1/works/${id}/publish`, { method: 'POST' }),

  offline: (id: number) => apiRequest<SilkWork>(`/api/v1/works/${id}/offline`, { method: 'POST' }),

  addEpisode: (id: number, body: SilkAddEpisodeBody) =>
    apiRequest<SilkEpisode>(`/api/v1/works/${id}/episodes`, { method: 'POST', body }),

  updateEpisode: (episodeId: number, body: SilkUpdateEpisodeBody) =>
    apiRequest<SilkEpisode>(`/api/v1/episodes/${episodeId}`, { method: 'PATCH', body }),

  removeEpisode: (episodeId: number) =>
    apiRequest<{ ok: boolean }>(`/api/v1/episodes/${episodeId}`, { method: 'DELETE' }),
}
